import React, { useState, useEffect, useCallback } from 'react';
import {
  Q5_OPTIONS, Q6_OPTIONS, Q_MINDSET_OPTIONS, Q12_OPTIONS, Q15_OPTIONS,
  Q19_OPTIONS, Q14_OPTIONS, Q16_OPTIONS,
  labelOf, normalizeRow,
} from '../constants/surveyOptions';

const API = process.env.REACT_APP_API_URL || '';

const p = {
  bg: '#09090b',
  surface: '#111113',
  card: '#18181b',
  border: '#27272a',
  borderHover: '#3f3f46',
  accent: '#6366f1',
  accentGlow: 'rgba(99,102,241,0.12)',
  text: '#f4f4f5',
  textMid: '#a1a1aa',
  textDim: '#52525b',
  green: '#4ade80',
  amber: '#fbbf24',
  red: '#f87171',
  cyan: '#22d3ee',
};

/* ── Helpers ── */
function parseArr(str) {
  try { return JSON.parse(str) || []; } catch { return str ? [str] : []; }
}
function pct(n, total) {
  return total === 0 ? 0 : Math.round((n / total) * 100);
}
function fmtDate(iso) {
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
function truncate(str, n) {
  if (!str) return '—';
  return str.length > n ? str.slice(0, n) + '…' : str;
}

/* ── Filter logic ── */
function matchesFilters(row, filters) {
  return Object.entries(filters).every(([field, value]) => {
    // q14 is stored as JSON array
    if (field === 'q14') return parseArr(row[field]).includes(value);
    return row[field] === value;
  });
}

/* ── Login ── */
function LoginScreen({ keyInput, setKeyInput, onLogin, error, loading }) {
  return (
    <div style={{
      minHeight: '100vh', background: p.bg, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
      fontFamily: 'system-ui, -apple-system, sans-serif',
    }}>
      <div style={{
        width: 360, background: p.surface, border: `1px solid ${p.border}`,
        borderRadius: 12, padding: '2.5rem',
        boxShadow: '0 25px 60px rgba(0,0,0,0.6)',
      }}>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '1.15rem', fontWeight: 700, letterSpacing: '0.1em', color: p.text, marginBottom: 6 }}>
            datum<span style={{ color: p.accent }}>o</span>tiv
          </div>
          <div style={{ fontSize: '0.7rem', color: p.textDim, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Admin Console</div>
        </div>
        <form onSubmit={onLogin}>
          <label style={{ display: 'block', fontSize: '0.72rem', color: p.textDim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>
            API Key
          </label>
          <input
            type="password"
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            placeholder="Enter admin key"
            autoFocus
            style={{
              width: '100%', boxSizing: 'border-box',
              background: p.card, border: `1px solid ${p.border}`, borderRadius: 6,
              color: p.text, fontSize: '0.88rem', padding: '0.65rem 0.85rem',
              outline: 'none', marginBottom: 14, fontFamily: 'monospace',
            }}
          />
          {error && <p style={{ color: p.red, fontSize: '0.78rem', margin: '0 0 12px' }}>{error}</p>}
          <button type="submit" disabled={loading} style={{
            width: '100%', background: p.accent, color: '#fff', border: 'none',
            borderRadius: 6, padding: '0.7rem', fontSize: '0.85rem', fontWeight: 600,
            cursor: 'pointer', letterSpacing: '0.05em', fontFamily: 'inherit',
          }}>
            {loading ? 'Authenticating…' : 'Sign in →'}
          </button>
        </form>
      </div>
    </div>
  );
}

/* ── Top Bar ── */
function TopBar({ onLogout, view, setView, counts, filterCount }) {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard' },
    { id: 'responses', label: `Responses (${counts.r})` },
    { id: 'subscribers', label: `Subscribers (${counts.s})` },
    { id: 'campaigns', label: 'Campaigns' },
  ];
  return (
    <header style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(9,9,11,0.92)', backdropFilter: 'blur(12px)',
      borderBottom: `1px solid ${p.border}`,
      display: 'flex', alignItems: 'center', gap: '1rem',
      padding: '0 1.75rem', height: 54,
    }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: '0.1em', color: p.text, marginRight: '0.5rem', whiteSpace: 'nowrap' }}>
        datum<span style={{ color: p.accent }}>o</span>tiv
        <span style={{ color: p.textDim, fontWeight: 400, fontSize: '0.68rem', marginLeft: 6, letterSpacing: '0.12em' }}>ADMIN</span>
      </div>
      <nav style={{ display: 'flex', gap: 4, flex: 1 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setView(t.id)} style={{
            background: view === t.id ? p.accentGlow : 'transparent',
            border: `1px solid ${view === t.id ? p.accent : 'transparent'}`,
            borderRadius: 6, color: view === t.id ? p.text : p.textMid,
            padding: '0.28rem 0.85rem', fontSize: '0.78rem', cursor: 'pointer',
            fontFamily: 'inherit', transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 6,
          }}>
            {t.label}
            {t.id === 'responses' && filterCount > 0 && (
              <span style={{
                background: p.accent, color: '#fff', borderRadius: 10,
                fontSize: '0.6rem', padding: '1px 6px', fontWeight: 700,
              }}>{filterCount}</span>
            )}
          </button>
        ))}
      </nav>
      <button onClick={onLogout} style={{
        background: 'transparent', border: `1px solid ${p.border}`, borderRadius: 6,
        color: p.textMid, padding: '0.28rem 0.75rem', fontSize: '0.73rem',
        cursor: 'pointer', fontFamily: 'inherit',
      }}>Sign out</button>
    </header>
  );
}

/* ── Stat Card ── */
function StatCard({ label, value, sub, color, onClick, active }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: active ? p.accentGlow : p.card,
        border: `1px solid ${active ? p.accent : p.border}`,
        borderRadius: 10, padding: '1.2rem 1.4rem',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color 0.15s, background 0.15s',
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.borderColor = active ? p.accent : p.borderHover; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.borderColor = active ? p.accent : p.border; }}
    >
      <div style={{ fontSize: '0.68rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 10 }}>{label}</div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: color || p.text, lineHeight: 1, marginBottom: 5 }}>{value}</div>
      {sub && <div style={{ fontSize: '0.73rem', color: p.textMid }}>{sub}</div>}
      {onClick && <div style={{ fontSize: '0.65rem', color: active ? p.accent : p.textDim, marginTop: 6 }}>{active ? 'Filter active — click to clear' : 'Click to filter'}</div>}
    </div>
  );
}

/* ── Bar Chart ── */
function BarChart({ data, total, color, field, filters, onFilter }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map(({ label, value, count }) => {
        const filterVal = value ?? label;
        const isActive = field && filters && filters[field] === filterVal;
        const isClickable = !!field && !!onFilter;
        return (
          <div
            key={label}
            onClick={isClickable ? () => onFilter(field, filterVal) : undefined}
            style={{ cursor: isClickable ? 'pointer' : 'default', borderRadius: 4, padding: '3px 4px', margin: '-3px -4px', background: isActive ? p.accentGlow : 'transparent', transition: 'background 0.15s' }}
            onMouseEnter={e => { if (isClickable && !isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.03)'; }}
            onMouseLeave={e => { if (isClickable && !isActive) e.currentTarget.style.background = 'transparent'; }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: '0.71rem', color: isActive ? p.text : p.textMid, maxWidth: '72%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: isActive ? 600 : 400 }}>{label}</span>
              <span style={{ fontSize: '0.71rem', color: isActive ? p.textMid : p.textDim }}>{count} · {pct(count, total)}%</span>
            </div>
            <div style={{ height: 3, background: p.border, borderRadius: 2 }}>
              <div style={{ height: 3, width: `${pct(count, total)}%`, background: isActive ? p.accent : (color || p.accent), borderRadius: 2, transition: 'width 0.7s ease' }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Chart Card ── */
function ChartCard({ title, children }) {
  return (
    <div style={{ background: p.card, border: `1px solid ${p.border}`, borderRadius: 10, padding: '1.2rem 1.4rem' }}>
      <div style={{ fontSize: '0.68rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 16 }}>{title}</div>
      {children}
    </div>
  );
}

/* ── Campaign Builder ── */
const PRESETS = [
  { label: 'Reddit · r/india',       source: 'reddit_india',    medium: 'forum'   },
  { label: 'Reddit · r/CarTalkIndia',source: 'reddit_cartalk',  medium: 'forum'   },
  { label: 'Reddit · r/bangalore',   source: 'reddit_bangalore',medium: 'forum'   },
  { label: 'Reddit · r/mumbai',      source: 'reddit_mumbai',   medium: 'forum'   },
  { label: 'Reddit · r/pune',        source: 'reddit_pune',     medium: 'forum'   },
  { label: 'LinkedIn post',          source: 'linkedin',        medium: 'social'  },
  { label: 'Facebook · car group',   source: 'facebook_cars',   medium: 'group'   },
  { label: 'WhatsApp · broadcast',   source: 'whatsapp',        medium: 'group'   },
  { label: 'Twitter / X',            source: 'twitter',         medium: 'social'  },
  { label: 'Direct / email',         source: 'direct',          medium: 'email'   },
];

const BASE_URL = 'https://datumotiv.com/survey';

function CampaignBuilder({ responses }) {
  const [preset, setPreset] = useState('');
  const [source, setSource] = useState('');
  const [medium, setMedium] = useState('');
  const [campaign, setCampaign] = useState('launch_mar26');
  const [copied, setCopied] = useState(false);

  function applyPreset(idx) {
    setPreset(idx);
    if (idx === '') return;
    const pr = PRESETS[Number(idx)];
    setSource(pr.source);
    setMedium(pr.medium);
  }

  const params = new URLSearchParams();
  if (source.trim())   params.set('utm_source',   source.trim());
  if (medium.trim())   params.set('utm_medium',   medium.trim());
  if (campaign.trim()) params.set('utm_campaign', campaign.trim());
  const url = params.toString() ? `${BASE_URL}?${params}` : BASE_URL;

  function copy() {
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Source breakdown from existing responses
  const sourceAcc = {};
  responses.forEach(r => {
    const s = r.utm_source || '(direct / unknown)';
    sourceAcc[s] = (sourceAcc[s] || 0) + 1;
  });
  const sourceData = Object.entries(sourceAcc).sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, count }));
  const total = responses.length;

  const inp = {
    width: '100%', boxSizing: 'border-box',
    background: p.card, border: `1px solid ${p.border}`, borderRadius: 6,
    color: p.text, fontSize: '0.83rem', padding: '0.55rem 0.75rem',
    outline: 'none', fontFamily: 'inherit',
  };
  const lbl = {
    display: 'block', fontSize: '0.65rem', color: p.textDim,
    textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 6,
  };

  return (
    <div style={{ padding: '1.75rem 2rem', maxWidth: 1000, margin: '0 auto' }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>

        {/* Builder card */}
        <div style={{ background: p.card, border: `1px solid ${p.border}`, borderRadius: 10, padding: '1.4rem' }}>
          <div style={{ fontSize: '0.68rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 18 }}>
            Generate Campaign URL
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={lbl}>Quick-pick channel</label>
            <select value={preset} onChange={e => applyPreset(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
              <option value="">— select a preset —</option>
              {PRESETS.map((pr, i) => <option key={i} value={i}>{pr.label}</option>)}
              <option value="custom">Custom…</option>
            </select>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
            <div>
              <label style={lbl}>utm_source</label>
              <input style={inp} value={source} onChange={e => setSource(e.target.value)} placeholder="reddit_india" />
            </div>
            <div>
              <label style={lbl}>utm_medium</label>
              <input style={inp} value={medium} onChange={e => setMedium(e.target.value)} placeholder="forum" />
            </div>
          </div>

          <div style={{ marginBottom: 18 }}>
            <label style={lbl}>utm_campaign</label>
            <input style={inp} value={campaign} onChange={e => setCampaign(e.target.value)} placeholder="launch_mar26" />
          </div>

          {/* Generated URL box */}
          <div style={{ background: p.surface, border: `1px solid ${p.border}`, borderRadius: 6, padding: '0.65rem 0.85rem', marginBottom: 10, wordBreak: 'break-all' }}>
            <span style={{ fontSize: '0.73rem', color: p.textMid, fontFamily: 'monospace' }}>{url}</span>
          </div>

          <button onClick={copy} style={{
            width: '100%', background: copied ? p.green : p.accent, color: '#fff',
            border: 'none', borderRadius: 6, padding: '0.6rem', fontSize: '0.83rem',
            fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', transition: 'background 0.2s',
          }}>
            {copied ? 'Copied!' : 'Copy link'}
          </button>

          <div style={{ marginTop: 14, fontSize: '0.7rem', color: p.textDim, lineHeight: 1.7 }}>
            <strong style={{ color: p.textMid }}>How it works:</strong> append this URL to any post. When someone clicks it, their UTM tags are
            stored with their survey response. GA4 also picks them up automatically — no extra setup needed.
          </div>
        </div>

        {/* Response source breakdown */}
        <div style={{ background: p.card, border: `1px solid ${p.border}`, borderRadius: 10, padding: '1.4rem' }}>
          <div style={{ fontSize: '0.68rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 18 }}>
            Responses by Source
          </div>
          {sourceData.length === 0 ? (
            <div style={{ color: p.textDim, fontSize: '0.82rem' }}>No tagged responses yet. Share a campaign link to start tracking.</div>
          ) : (
            <BarChart data={sourceData} total={total} color={p.accent} />
          )}

          {sourceData.length > 0 && (
            <div style={{ marginTop: 20, fontSize: '0.68rem', color: p.textDim, lineHeight: 1.7 }}>
              <strong style={{ color: p.textMid }}>Bias note:</strong> responses from forum posts (Reddit) tend to skew younger and
              more tech-savvy than WhatsApp/email outreach. Tag every campaign and filter by source in Responses to spot skew.
            </div>
          )}
        </div>
      </div>

      {/* Posting guide */}
      <div style={{ marginTop: 16, background: p.card, border: `1px solid ${p.border}`, borderRadius: 10, padding: '1.4rem' }}>
        <div style={{ fontSize: '0.68rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 14 }}>
          Recommended Forums &amp; Posting Strategy
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { platform: 'Reddit', channels: ['r/india', 'r/CarTalkIndia', 'r/bangalore', 'r/mumbai', 'r/pune', 'r/hyderabad'], note: 'Post in weekday evenings IST. Be transparent — "I\'m building X, 5-min survey". Short, honest posts perform better than pitches.' },
            { platform: 'Facebook Groups', channels: ['Car Owners India', 'Used Cars India', 'City-specific car groups'], note: 'Search for groups with 10k–100k members. Smaller groups get higher engagement. Ask the admin for permission first to avoid removal.' },
            { platform: 'WhatsApp / LinkedIn', channels: ['Personal network broadcast', 'LinkedIn post', 'Startup communities'], note: 'Use personal voice — "I\'m building something and need 5 mins of your time". LinkedIn posts with a story outperform plain links.' },
          ].map(({ platform, channels, note }) => (
            <div key={platform} style={{ background: p.surface, borderRadius: 8, padding: '1rem', border: `1px solid ${p.border}` }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: p.text, marginBottom: 8 }}>{platform}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginBottom: 10 }}>
                {channels.map(c => (
                  <span key={c} style={{ fontSize: '0.7rem', color: p.accent, background: p.accentGlow, borderRadius: 4, padding: '1px 6px', display: 'inline-block', width: 'fit-content' }}>{c}</span>
                ))}
              </div>
              <div style={{ fontSize: '0.7rem', color: p.textDim, lineHeight: 1.65 }}>{note}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Dashboard ── */
function Dashboard({ responses, subscribers, filters, onFilter }) {
  const completed = responses.filter(r => r.q1 === 'yes');
  const n = completed.length;

  const tally = (key, rows = completed) => {
    const acc = {};
    rows.forEach(r => { if (r[key]) acc[r[key]] = (acc[r[key]] || 0) + 1; });
    return acc;
  };
  const sortDesc = obj => Object.entries(obj).sort((a, b) => b[1] - a[1]).map(([label, count]) => ({ label, value: label, count }));
  const ordered = (keys, obj) => keys.map(l => ({ label: l, value: l, count: obj[l] || 0 }));

  const cities = sortDesc(tally('q2')).slice(0, 6);

  const fromOpts = (opts, field) => {
    const counts = tally(field);
    return opts.map(({ key, label }) => ({ key, label, value: key, count: counts[key] || 0 }));
  };

  const spendData   = fromOpts(Q5_OPTIONS,        'q5');
  const shockData   = fromOpts(Q6_OPTIONS,        'q6');
  const ageData     = fromOpts(Q19_OPTIONS,       'q19');
  const bdData      = fromOpts(Q12_OPTIONS,       'q12');

  const mindsetData = fromOpts(Q_MINDSET_OPTIONS, 'q_mindset').map(d => ({
    ...d,
    label: { mindset_tool: 'Tool (casual)', mindset_resp: 'Responsible', mindset_know: 'Knowledgeable', mindset_enth: 'Enthusiast' }[d.key] || d.label,
  }));

  const wtpData = fromOpts(Q15_OPTIONS, 'q15').map(d => ({
    ...d,
    label: { wtp_none: 'Nothing', wtp_low: '₹200–500/yr', wtp_mid: '₹500–1,500/yr', wtp_high: '₹1,500–5,000/yr', wtp_vhigh: '>₹5,000/yr' }[d.key] || d.label,
  }));

  const priorityAcc = {};
  completed.forEach(r => {
    parseArr(r.q14).forEach(v => { priorityAcc[v] = (priorityAcc[v] || 0) + 1; });
  });
  const priorityData = Q14_OPTIONS.map(({ key, label }) => ({
    key, value: key, label: label.split('—')[0].trim(), count: priorityAcc[key] || 0,
  })).sort((a, b) => b.count - a.count);

  const toolYes = completed.filter(r => r.q16 === 'tool_yes').length;
  const toolActive = filters['q16'] === 'tool_yes';

  return (
    <div style={{ padding: '1.75rem 2rem', maxWidth: 1200, margin: '0 auto' }}>
      {/* Stat row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard label="Total Responses" value={responses.length} sub={`${completed.length} qualified car owners`} color={p.accent} />
        <StatCard label="Subscribers" value={subscribers.length} sub="Email early access" />
        <StatCard label="Cities" value={Object.keys(tally('q2')).length} sub={cities[0] ? `Top: ${cities[0].label}` : '—'} />
        <StatCard
          label="Use Monitoring Tool"
          value={`${pct(toolYes, n)}%`}
          sub={`${toolYes} of ${n} said yes`}
          color={p.green}
          active={toolActive}
          onClick={() => onFilter('q16', 'tool_yes')}
        />
      </div>

      {/* Charts row 1 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <ChartCard title="Maintenance Spend — Last 12 Months">
          <BarChart data={spendData} total={n} color={p.amber} field="q5" filters={filters} onFilter={onFilter} />
        </ChartCard>
        <ChartCard title="Bill Shock Frequency">
          <BarChart data={shockData} total={n} color={p.red} field="q6" filters={filters} onFilter={onFilter} />
        </ChartCard>
        <ChartCard title="Top Priority (Q14)">
          <BarChart data={priorityData} total={n} color={p.accent} field="q14" filters={filters} onFilter={onFilter} />
        </ChartCard>
      </div>

      {/* Charts row 2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
        <ChartCard title="Age Distribution">
          <BarChart data={ageData} total={n} color={p.cyan} field="q19" filters={filters} onFilter={onFilter} />
        </ChartCard>
        <ChartCard title="Breakdown History">
          <BarChart data={bdData} total={n} color={p.red} field="q12" filters={filters} onFilter={onFilter} />
        </ChartCard>
        <ChartCard title="Top Cities">
          <BarChart data={cities} total={n} color={p.green} field="q2" filters={filters} onFilter={onFilter} />
        </ChartCard>
      </div>

      {/* Charts row 3 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ChartCard title="Owner Mindset">
          <BarChart data={mindsetData} total={n} color={p.cyan} field="q_mindset" filters={filters} onFilter={onFilter} />
        </ChartCard>
        <ChartCard title="Willingness to Pay — Preventive Alerts (Q15)">
          <BarChart data={wtpData} total={n} color={p.green} field="q15" filters={filters} onFilter={onFilter} />
        </ChartCard>
      </div>
    </div>
  );
}

/* ── Q Labels ── */
const QL = {
  q1: 'Owns car', q2: 'City', q3: 'Car (make & model)', q_own: 'Year bought',
  q4: 'Odometer', q_mindset: 'Owner mindset', q5: 'Maint. spend (12mo)', q6: 'Bill shock freq.',
  q7: 'Saw last repair coming?', q8: 'Understood mechanic?', q9: 'Suspected overcharge?',
  q10: 'When to service (multi)', q11: 'First action when feels off',
  q12: 'Breakdown history', q13: 'Biggest frustration',
  q14: 'Top priority', q15: 'Value of advance warning',
  q16: 'Uses monitoring tool?', q17: 'Which tool?',
  q18: 'How to discover solution (multi)', q19: 'Age', q20: 'Gender',
  q21: 'Profession', q22: 'Education', q23: 'Other thoughts / contact',
  utm_source: 'Campaign source', utm_medium: 'Campaign medium', utm_campaign: 'Campaign name',
  ip_address: 'IP address', user_agent: 'User agent', screen_resolution: 'Screen resolution',
  referrer: 'Referrer', timezone: 'Timezone', language: 'Browser language',
  ga_client_id: 'GA4 client ID',
};

const ALL_FIELDS = ['q1','q2','q3','q_own','q4','q_mindset','q5','q6','q7','q8','q9','q10','q11','q12','q13','q14','q15','q16','q17','q18','q19','q20','q21','q22','q23','utm_source','utm_medium','utm_campaign','ip_address','user_agent','screen_resolution','referrer','timezone','language','ga_client_id'];

/* ── Filter chip label helpers ── */
const FIELD_LABELS = {
  q2: 'City', q5: 'Spend', q6: 'Bill shock', q14: 'Priority',
  q19: 'Age', q12: 'Breakdown', q_mindset: 'Mindset', q15: 'WTP',
  q16: 'Uses tool',
};
const MINDSET_DISPLAY = { mindset_tool: 'Tool (casual)', mindset_resp: 'Responsible', mindset_know: 'Knowledgeable', mindset_enth: 'Enthusiast' };
const WTP_DISPLAY     = { wtp_none: 'Nothing', wtp_low: '₹200–500/yr', wtp_mid: '₹500–1,500/yr', wtp_high: '₹1,500–5,000/yr', wtp_vhigh: '>₹5,000/yr' };
const Q14_DISPLAY     = Object.fromEntries(Q14_OPTIONS.map(({ key, label }) => [key, label.split('—')[0].trim()]));
const Q16_DISPLAY     = { tool_yes: 'Yes', tool_no: 'No' };
const AGE_DISPLAY     = Object.fromEntries(Q19_OPTIONS.map(({ key, label }) => [key, label]));

function chipLabel(field, value) {
  const fLabel = FIELD_LABELS[field] || QL[field] || field;
  let vLabel = value;
  if (field === 'q_mindset') vLabel = MINDSET_DISPLAY[value] || value;
  else if (field === 'q15')  vLabel = WTP_DISPLAY[value]     || value;
  else if (field === 'q14')  vLabel = Q14_DISPLAY[value]     || value;
  else if (field === 'q16')  vLabel = Q16_DISPLAY[value]     || value;
  else if (field === 'q19')  vLabel = AGE_DISPLAY[value]     || value;
  else if (vLabel.length > 20) vLabel = vLabel.slice(0, 20) + '…';
  return `${fLabel}: ${vLabel}`;
}

/* ── Detail Side Panel ── */
function DetailPanel({ row, onClose }) {
  if (!row) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, bottom: 0, width: 420,
      background: p.surface, borderLeft: `1px solid ${p.border}`,
      overflowY: 'auto', zIndex: 200, padding: '1.5rem',
      boxShadow: '-20px 0 60px rgba(0,0,0,0.5)',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
        <div>
          <div style={{ fontSize: '0.7rem', color: p.textDim, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>Response #{row.id}</div>
          <div style={{ fontSize: '0.95rem', color: p.text, fontWeight: 600 }}>{row.q2 || '—'}</div>
          <div style={{ fontSize: '0.8rem', color: p.textMid }}>{row.q3 || '—'}</div>
        </div>
        <button onClick={onClose} style={{
          background: 'transparent', border: `1px solid ${p.border}`, borderRadius: 6,
          color: p.textMid, width: 30, height: 30, cursor: 'pointer', fontSize: '0.85rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>✕</button>
      </div>
      <div style={{ fontSize: '0.7rem', color: p.textDim, marginBottom: '1.25rem' }}>{fmtDate(row.created_at)}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {ALL_FIELDS.map(key => {
          let val = row[key];
          if (!val) return null;
          if (key === 'q10' || key === 'q14' || key === 'q18') val = parseArr(val).join(' · ');
          return (
            <div key={key} style={{ borderBottom: `1px solid ${p.border}`, paddingBottom: 10 }}>
              <div style={{ fontSize: '0.65rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 3 }}>{QL[key] || key}</div>
              <div style={{ fontSize: '0.82rem', color: p.textMid, lineHeight: 1.55 }}>{val}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Responses Table ── */
function ResponsesTable({ responses, selected, setSelected, filters, onFilter }) {
  const filtered = responses.filter(r => matchesFilters(r, filters));
  const hasFilters = Object.keys(filters).length > 0;
  const cols = ['#', 'Date', 'Source', 'City', 'Car', 'Yr', 'Odometer', 'Spend', 'Bill shock', 'Top priority', 'Frustration', 'Age', 'Gender'];

  return (
    <div style={{ padding: '1.25rem 2rem', position: 'relative' }}>
      <DetailPanel row={selected} onClose={() => setSelected(null)} />

      {/* Filter chips */}
      {hasFilters && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
          <span style={{ fontSize: '0.7rem', color: p.textDim, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
            Filtered — {filtered.length} of {responses.length}
          </span>
          {Object.entries(filters).map(([field, value]) => (
            <button
              key={field}
              onClick={() => onFilter(field, value)}
              style={{
                background: p.accentGlow, border: `1px solid ${p.accent}`,
                borderRadius: 20, color: p.text, fontSize: '0.72rem',
                padding: '0.2rem 0.65rem', cursor: 'pointer', fontFamily: 'inherit',
                display: 'flex', alignItems: 'center', gap: 5,
              }}
            >
              {chipLabel(field, value)} <span style={{ color: p.textDim }}>✕</span>
            </button>
          ))}
          <button
            onClick={() => Object.keys(filters).forEach(f => onFilter(f, filters[f]))}
            style={{
              background: 'transparent', border: `1px solid ${p.border}`,
              borderRadius: 20, color: p.textMid, fontSize: '0.7rem',
              padding: '0.2rem 0.65rem', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >Clear all</button>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.79rem', fontFamily: 'system-ui, sans-serif' }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${p.border}` }}>
              {cols.map(h => (
                <th key={h} style={{
                  textAlign: 'left', padding: '0.55rem 0.75rem',
                  color: p.textDim, fontWeight: 500, fontSize: '0.67rem',
                  textTransform: 'uppercase', letterSpacing: '0.1em', whiteSpace: 'nowrap',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => {
              const priority = parseArr(r.q14)[0];
              const shortPriority = priority ? priority.split('—')[0].trim() : '—';
              const isSelected = selected?.id === r.id;
              const isHighShock = r.q6 === 'Almost always' || r.q6 === 'Often';
              return (
                <tr
                  key={r.id}
                  onClick={() => setSelected(isSelected ? null : r)}
                  style={{
                    borderBottom: `1px solid ${p.border}`,
                    background: isSelected ? p.accentGlow : 'transparent',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = p.surface; }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                >
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textDim }}>{r.id}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid, whiteSpace: 'nowrap' }}>{fmtDate(r.created_at)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', whiteSpace: 'nowrap' }}>
                    {r.utm_source
                      ? <span style={{ background: p.accentGlow, border: `1px solid ${p.accent}`, borderRadius: 4, color: p.accent, fontSize: '0.65rem', padding: '1px 6px', fontFamily: 'monospace' }}>{r.utm_source}</span>
                      : <span style={{ color: p.textDim, fontSize: '0.72rem' }}>direct</span>
                    }
                  </td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.text, whiteSpace: 'nowrap' }}>{r.q2 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.text, whiteSpace: 'nowrap' }}>{r.q3 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid }}>{r.q_own || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid }}>{r.q4 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid, whiteSpace: 'nowrap' }}>{r.q5 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: isHighShock ? p.red : p.textMid, whiteSpace: 'nowrap' }}>{r.q6 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortPriority}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{truncate(r.q13, 60)}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid, whiteSpace: 'nowrap' }}>{r.q19 || '—'}</td>
                  <td style={{ padding: '0.6rem 0.75rem', color: p.textMid }}>{r.q20 || '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div style={{ textAlign: 'center', padding: '5rem', color: p.textDim, fontSize: '0.85rem' }}>
            {hasFilters ? 'No responses match the current filters.' : 'No responses yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Subscribers Table ── */
function SubscribersTable({ subscribers }) {
  return (
    <div style={{ padding: '1.25rem 2rem' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${p.border}` }}>
            {['#', 'Email', 'Subscribed'].map(h => (
              <th key={h} style={{
                textAlign: 'left', padding: '0.55rem 0.75rem', color: p.textDim,
                fontWeight: 500, fontSize: '0.67rem', textTransform: 'uppercase', letterSpacing: '0.1em',
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {subscribers.map(s => (
            <tr key={s.id} style={{ borderBottom: `1px solid ${p.border}` }}>
              <td style={{ padding: '0.65rem 0.75rem', color: p.textDim }}>{s.id}</td>
              <td style={{ padding: '0.65rem 0.75rem', color: p.text, fontFamily: 'monospace', fontSize: '0.82rem' }}>{s.email}</td>
              <td style={{ padding: '0.65rem 0.75rem', color: p.textMid }}>{fmtDate(s.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {subscribers.length === 0 && (
        <div style={{ textAlign: 'center', padding: '5rem', color: p.textDim, fontSize: '0.85rem' }}>No subscribers yet.</div>
      )}
    </div>
  );
}

/* ── Root ── */
export default function Admin() {
  const [apiKey, setApiKey] = useState(() => sessionStorage.getItem('dm_admin_key') || '');
  const [keyInput, setKeyInput] = useState('');
  const [authed, setAuthed] = useState(!!sessionStorage.getItem('dm_admin_key'));
  const [responses, setResponses] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('dashboard');
  const [filters, setFilters] = useState({});

  const fetchData = useCallback(async (key) => {
    setLoading(true);
    setError('');
    try {
      const headers = { 'X-API-Key': key };
      const [rRes, sRes] = await Promise.all([
        fetch(`${API}/admin/responses`, { headers }),
        fetch(`${API}/admin/subscribers`, { headers }),
      ]);
      if (rRes.status === 403) {
        setError('Invalid API key.');
        sessionStorage.removeItem('dm_admin_key');
        setAuthed(false);
        return;
      }
      setResponses((await rRes.json()).map(normalizeRow));
      setSubscribers(await sRes.json());
      sessionStorage.setItem('dm_admin_key', key);
      setAuthed(true);
    } catch {
      setError('Failed to connect to API.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authed && apiKey) fetchData(apiKey);
  }, []); // eslint-disable-line

  function login(e) {
    e.preventDefault();
    const key = keyInput.trim();
    if (!key) return;
    setApiKey(key);
    fetchData(key);
  }

  function logout() {
    sessionStorage.removeItem('dm_admin_key');
    setAuthed(false);
    setApiKey('');
    setKeyInput('');
    setResponses([]);
    setSubscribers([]);
    setFilters({});
  }

  function toggleFilter(field, value) {
    setFilters(prev => {
      const next = { ...prev };
      if (next[field] === value) {
        delete next[field];  // toggle off
      } else {
        next[field] = value; // set
      }
      return next;
    });
    setView('responses');
  }

  if (!authed) {
    return <LoginScreen keyInput={keyInput} setKeyInput={setKeyInput} onLogin={login} error={error} loading={loading} />;
  }

  return (
    <div style={{ background: p.bg, minHeight: '100vh', fontFamily: 'system-ui, -apple-system, sans-serif', color: p.text }}>
      <TopBar
        onLogout={logout} view={view} setView={setView}
        counts={{ r: responses.length, s: subscribers.length }}
        filterCount={Object.keys(filters).length}
      />
      {loading && <div style={{ textAlign: 'center', padding: '5rem', color: p.textMid, fontSize: '0.85rem' }}>Loading…</div>}
      {!loading && view === 'dashboard' && (
        <Dashboard responses={responses} subscribers={subscribers} filters={filters} onFilter={toggleFilter} />
      )}
      {!loading && view === 'responses' && (
        <ResponsesTable responses={responses} selected={selected} setSelected={setSelected} filters={filters} onFilter={toggleFilter} />
      )}
      {!loading && view === 'subscribers' && <SubscribersTable subscribers={subscribers} />}
      {view === 'campaigns' && <CampaignBuilder responses={responses} />}
    </div>
  );
}
